import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  createZoomMeeting,
  fetchZoomMeetings,
  cancelZoomMeeting,
  deleteZoomMeeting,
  sendZoomReminder,
  searchUserEmails,
} from "./adminZoomService";
import "./zoomMeetings.css";

const EMPTY_FORM = {
  topic: "",
  agenda: "",
  startTime: "",
  durationMinutes: 45,
  recipientEmails: [],
};

const AdminZoomMeetingsPage = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [emailInput, setEmailInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [meetings, setMeetings] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [confirmReminder, setConfirmReminder] = useState(null);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const debounceRef = useRef(null);
  const dropdownRef = useRef(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  const loadMeetings = useCallback(async () => {
    setListLoading(true);
    const res = await fetchZoomMeetings();
    if (res.status === 200) setMeetings(Array.isArray(res.data) ? res.data : res.data?.data || []);
    setListLoading(false);
  }, []);

  useEffect(() => { loadMeetings(); }, [loadMeetings]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setSuggestions([]);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleEmailInputChange = (e) => {
    const val = e.target.value;
    setEmailInput(val);
    clearTimeout(debounceRef.current);
    if (val.trim().length < 2) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSuggestLoading(true);
      const res = await searchUserEmails(val.trim());
      if (res.status === 200) {
        const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setSuggestions(data.filter((u) => !form.recipientEmails.includes(u.email)));
      }
      setSuggestLoading(false);
    }, 350);
  };

  const addEmail = (email) => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || form.recipientEmails.includes(trimmed)) return;
    setForm((prev) => ({ ...prev, recipientEmails: [...prev.recipientEmails, trimmed] }));
    setEmailInput("");
    setSuggestions([]);
  };

  const handleEmailKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (emailInput.trim()) addEmail(emailInput);
    }
  };

  const removeEmail = (email) => {
    setForm((prev) => ({ ...prev, recipientEmails: prev.recipientEmails.filter((e) => e !== email) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.topic.trim()) return showToast("error", "Topic is required");
    if (!form.startTime) return showToast("error", "Start time is required");
    if (form.recipientEmails.length === 0) return showToast("error", "Add at least one recipient email");

    setSubmitting(true);
    const res = await createZoomMeeting({
      topic: form.topic.trim(),
      agenda: form.agenda.trim(),
      startTime: new Date(form.startTime).toISOString(),
      durationMinutes: Number(form.durationMinutes),
      displayTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      recipientEmails: form.recipientEmails,
    });
    setSubmitting(false);

    if (res.status === 200 || res.status === 201) {
      showToast("success", `✅ Meeting created! Join: ${res.data?.zoomJoinUrl || res.data?.data?.zoomJoinUrl}`);
      setForm(EMPTY_FORM);
      loadMeetings();
    } else {
      showToast("error", res.message || "Failed to create meeting");
    }
  };

  const handleCancel = async (id) => {
    const res = await cancelZoomMeeting(id);
    setConfirmCancel(null);
    if (res.status === 200) {
      showToast("success", "Meeting cancelled");
      setMeetings((prev) => prev.map((m) => m.id === id ? { ...m, status: "CANCELLED" } : m));
    } else {
      showToast("error", res.message || "Failed to cancel meeting");
    }
  };

  const handleSendReminder = async (meeting) => {
    setReminderLoading(true);
    const res = await sendZoomReminder(meeting.id);
    setReminderLoading(false);
    setConfirmReminder(null);
    if (res.status === 200) {
      showToast("success", `🔔 Reminder sent to ${meeting.recipientEmails?.length || 0} recipients`);
    } else {
      showToast("error", res.data?.error || res.message || "Failed to send reminder");
    }
  };

  const handleDelete = async (meeting) => {
    setDeleteLoading(true);
    const res = await deleteZoomMeeting(meeting.id);
    setDeleteLoading(false);
    setConfirmDelete(null);
    if (res.status === 200) {
      showToast("success", "Meeting deleted successfully");
      setMeetings((prev) => prev.filter((m) => m.id !== meeting.id));
    } else {
      showToast("error", res.data?.error || res.message || "Failed to delete meeting");
    }
  };

  const formatDateTime = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  };

  const RecipientsCell = ({ emails }) => {
    const [showAll, setShowAll] = useState(false);
    if (!emails?.length) return <span style={{ color: "#9ca3af" }}>—</span>;
    const visible = showAll ? emails : emails.slice(0, 2);
    return (
      <div className="zm-recipients-cell">
        {visible.map((e) => <span key={e} className="zm-email-chip-sm">{e}</span>)}
        {!showAll && emails.length > 2 && (
          <button className="zm-more-btn" onClick={() => setShowAll(true)}>+{emails.length - 2} more</button>
        )}
      </div>
    );
  };

  return (
    <div className="zm-page">
      {toast && (
        <div className={`zm-toast zm-toast--${toast.type}`}>{toast.message}</div>
      )}

      <div className="zm-header">
        <h2>📹 Zoom Meetings</h2>
      </div>

      <div className="zm-layout">
        {/* CREATE FORM */}
        <div className="zm-form-panel">
          <h3 className="zm-panel-title">Create Meeting</h3>
          <form onSubmit={handleSubmit} className="zm-form">

            <div className="zm-field">
              <label>Topic <span className="zm-req">*</span></label>
              <input
                type="text"
                placeholder="e.g. 1-on-1 Speaking Session"
                value={form.topic}
                onChange={(e) => setForm((p) => ({ ...p, topic: e.target.value }))}
              />
            </div>

            <div className="zm-field">
              <label>Agenda</label>
              <textarea
                placeholder="Optional agenda or notes..."
                value={form.agenda}
                rows={12}
                onChange={(e) => setForm((p) => ({ ...p, agenda: e.target.value }))}
              />
            </div>

            <div className="zm-field">
              <label>Start Time <span className="zm-req">*</span></label>
              <input
                type="datetime-local"
                value={form.startTime}
                onChange={(e) => setForm((p) => ({ ...p, startTime: e.target.value }))}
              />
            </div>

            <div className="zm-field">
              <label>Duration (minutes) <span className="zm-req">*</span></label>
              <input
                type="number"
                min={5}
                max={480}
                value={form.durationMinutes}
                onChange={(e) => setForm((p) => ({ ...p, durationMinutes: e.target.value }))}
              />
            </div>

            <div className="zm-field" ref={dropdownRef}>
              <label>Recipient Emails <span className="zm-req">*</span></label>
              <div className="zm-email-tags">
                {form.recipientEmails.map((email) => (
                  <span key={email} className="zm-email-chip">
                    {email}
                    <button type="button" onClick={() => removeEmail(email)}>×</button>
                  </span>
                ))}
                <input
                  type="text"
                  className="zm-email-input"
                  placeholder="Search name/email or type & press Enter"
                  value={emailInput}
                  onChange={handleEmailInputChange}
                  onKeyDown={handleEmailKeyDown}
                />
              </div>
              {(suggestions.length > 0 || suggestLoading) && (
                <div className="zm-dropdown">
                  {suggestLoading && <div className="zm-dropdown-item zm-dropdown-loading">Searching...</div>}
                  {suggestions.map((u) => (
                    <div
                      key={u.id || u.email}
                      className="zm-dropdown-item"
                      onMouseDown={() => addEmail(u.email)}
                    >
                      <span className="zm-suggest-name">{u.name}</span>
                      <span className="zm-suggest-email">{u.email}</span>
                    </div>
                  ))}
                </div>
              )}
              <p className="zm-hint">Search existing users or type any email and press Enter</p>
            </div>

            <button type="submit" className="zm-btn-primary" disabled={submitting}>
              {submitting ? "Creating..." : "📨 Create & Send Invites"}
            </button>
          </form>
        </div>

        {/* MEETINGS TABLE */}
        <div className="zm-list-panel">
          <h3 className="zm-panel-title">All Meetings</h3>
          {listLoading ? (
            <p className="zm-loading">Loading meetings...</p>
          ) : meetings.length === 0 ? (
            <div className="zm-empty">
              <span>📹</span>
              <p>No meetings yet. Create one to get started.</p>
            </div>
          ) : (
            <div className="zm-table-wrap">
              <table className="zm-table">
                <thead>
                  <tr>
                    <th>Topic</th>
                    <th>Start Time</th>
                    <th>Duration</th>
                    <th>Recipients</th>
                    <th>Status</th>
                    <th>Zoom Link</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {meetings.map((m) => (
                    <tr key={m.id}>
                      <td>
                        <div className="zm-topic">{m.topic}</div>
                        {m.agenda && <div className="zm-agenda">{m.agenda}</div>}
                      </td>
                      <td>{formatDateTime(m.startTime)}</td>
                      <td>{m.durationMinutes} min</td>
                      <td><RecipientsCell emails={m.recipientEmails} /></td>
                      <td>
                        <span className={`zm-badge zm-badge--${(m.status || "").toLowerCase()}`}>
                          {m.status || "—"}
                        </span>
                      </td>
                      <td>
                        {m.zoomJoinUrl ? (
                          <div className="zm-link-cell">
                            <a href={m.zoomJoinUrl} target="_blank" rel="noreferrer" className="zm-join-link">
                              🔗 Join
                            </a>
                            <button
                              className={`zm-copy-btn ${copiedId === m.id ? "zm-copy-btn--copied" : ""}`}
                              onClick={() => {
                                navigator.clipboard.writeText(m.zoomJoinUrl);
                                setCopiedId(m.id);
                                setTimeout(() => setCopiedId(null), 2000);
                              }}
                              title="Copy link"
                            >
                              {copiedId === m.id ? "✅ Copied" : "📋 Copy"}
                            </button>
                          </div>
                        ) : <span style={{ color: "#9ca3af" }}>—</span>}
                      </td>
                      <td>
                        <div className="zm-actions">
                          {m.status === "SCHEDULED" && (
                            <button
                              className="zm-btn-reminder"
                              onClick={() => setConfirmReminder(m)}
                            >
                              🔔 Remind
                            </button>
                          )}
                          <button
                            className="zm-btn-cancel"
                            disabled={m.status === "CANCELLED"}
                            onClick={() => setConfirmCancel(m)}
                          >
                            Cancel
                          </button>
                          <button
                            className="zm-btn-delete"
                            onClick={() => setConfirmDelete(m)}
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* CONFIRM DELETE DIALOG */}
      {confirmDelete && (
        <div className="zm-overlay">
          <div className="zm-dialog">
            <h4>Delete Meeting?</h4>
            <p>Are you sure you want to delete <strong>{confirmDelete.topic}</strong>? This will also remove it from Zoom and cannot be undone.</p>
            <div className="zm-dialog-actions">
              <button className="zm-btn-secondary" onClick={() => setConfirmDelete(null)} disabled={deleteLoading}>Cancel</button>
              <button className="zm-btn-danger" onClick={() => handleDelete(confirmDelete)} disabled={deleteLoading}>
                {deleteLoading ? "Deleting..." : "🗑️ Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONFIRM REMINDER DIALOG */}
      {confirmReminder && (
        <div className="zm-overlay">
          <div className="zm-dialog">
            <h4>Send Reminder?</h4>
            <p>
              Send reminder email to all <strong>{confirmReminder.recipientEmails?.length || 0} recipients</strong> of <strong>{confirmReminder.topic}</strong>?
            </p>
            <div className="zm-dialog-actions">
              <button className="zm-btn-secondary" onClick={() => setConfirmReminder(null)} disabled={reminderLoading}>Cancel</button>
              <button className="zm-btn-reminder-confirm" onClick={() => handleSendReminder(confirmReminder)} disabled={reminderLoading}>
                {reminderLoading ? "Sending..." : "🔔 Send"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONFIRM CANCEL DIALOG */}
      {confirmCancel && (
        <div className="zm-overlay">
          <div className="zm-dialog">
            <h4>Cancel Meeting?</h4>
            <p>Are you sure you want to cancel <strong>{confirmCancel.topic}</strong>? This cannot be undone.</p>
            <div className="zm-dialog-actions">
              <button className="zm-btn-secondary" onClick={() => setConfirmCancel(null)}>No, Keep It</button>
              <button className="zm-btn-danger" onClick={() => handleCancel(confirmCancel.id)}>Yes, Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminZoomMeetingsPage;
